import React, { useState } from "react";
import { Modal, Button, Alert, Spinner, Form } from "react-bootstrap";
import { cancelVirtTransfer } from "../../http/userAPI";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";

const REASONS_MAP = {
    sender_blocked: "Sender Account Blocked. Fraud suspected.",
    illegal_activity: "Illegal activity / Violation of Terms of Service.",
    pending_cancel: "Cancel Pending Transfer — no balance change.",
    recipient_report: "Customer reported unauthorized transfer.",
    other: "", // текст возьмем из customReason
};

const CancelTransferModal = ({ show, onHide, transfer, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    // причина отмены
    const [reasonKey, setReasonKey] = useState("");
    const [customReason, setCustomReason] = useState("");

    if (!transfer) return null;

    const stamp =
        typeof transfer.created_at === "number"
            ? new Date(transfer.created_at * 1000)
            : new Date(transfer.created_at);

    const buildReasonText = () => {
        if (reasonKey === "other") return customReason.trim();
        return REASONS_MAP[reasonKey] || "";
    };

    const handleClose = () => {
        setLoading(false);
        setErr("");
        setDone(false);
        setReasonKey("");
        setCustomReason("");
        onHide?.();
    }

    const handleCancel = async () => {
        setLoading(true);
        setErr("");

        const reasonText = buildReasonText();
        if (!reasonText) {
            setLoading(false);
            setErr("Please select a reason for cancellation (or specify it in 'Other').");
            return;
        }

        try {
            const data = await cancelVirtTransfer(transfer.id, {
                reason: reasonText,
            });
            setDone(true);
            onSuccess?.(data.item);
        } catch (e) {
            setErr(e?.response?.data?.message || e?.message || "Failed to cancel transfer");
        } finally {
            setLoading(false);
        }
    };

    const cancelDisabled =
        loading ||
        transfer.status === "CANCELED" ||
        !buildReasonText() || err || done;

    return (
        <Modal show={show} onHide={handleClose} size="" top>
            <Modal.Header closeButton className="py-2">
                <Modal.Title className="fs-6">Transfer Actions</Modal.Title>
            </Modal.Header>

            <Modal.Body className="py-2">




                {/* Details */}
                <div className="small mb-2">
                    <div className="text-muted">
                        <b>ID:</b> {transfer.id} | <b>Date:</b>{" "}
                        {isNaN(stamp) ? "—" : format(stamp, "dd-MMM-yyyy HH:mm", { locale: enUS })}
                    </div>

                    <div className="text-muted">
                        <b>From → To:</b>{" "}
                        #{transfer.sender_id} {transfer.sender_username || ""} {" → "}#
                        {transfer.receiver_id} {transfer.receiver_username || ""}
                    </div>

                    <div><b className="text-muted">Sender:</b></div>
                    <div className="p-2 mb-2 rounded-2 border border-1 border-secondary-subtle bg-light-subtle border-start border-3 border-info">
                        <div>
                            <span className="text-muted fw-semibold">Sender:</span> #{transfer.sender_id} | {transfer.sender_username || "—"} | {transfer.sender_email || "—"}
                        </div>
                        <div>
                            <span className="text-muted fw-semibold">Sender Balance (Now):</span> {transfer.sender_balance ?? "—"} Virt
                        </div>
                    </div>

                    <div><b className="text-muted">Receiver:</b></div>
                    <div className="p-2 mb-2 rounded-2 border border-1 border-secondary-subtle bg-light-subtle border-start border-3 border-success">
                        <div>
                            <span className="text-muted fw-semibold">Receiver:</span>{" "}
                            #{transfer.receiver_id} | {transfer.receiver_username ?? "—"} | {transfer.to_email ?? "—"}
                        </div>
                        <div>
                            <span className="text-muted fw-semibold">Receiver Balance (Now):</span>{" "}
                            {transfer.receiver_balance ?? "—"} Virt
                        </div>
                    </div>

                    <div className="text-muted"><b>Amount:</b> {transfer.amount} Virt</div>
                    <div className="text-muted"><b>Status:</b> {transfer.status}</div>
                    <div className="text-muted"><b>Comment:</b> {transfer.description || "—"}</div>
                </div>
                {/* Cancellation Reason */}
                <div className="mb-2">
                    <Form.Label className="small mb-1 fw-semibold">Cancellation Reason:</Form.Label>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                        <Form.Select
                            size="sm"
                            value={reasonKey}
                            onChange={(e) => setReasonKey(e.target.value)}
                            style={{ minWidth: 260 }}
                        >
                            <option value="">Select reason…</option>
                            <option value="sender_blocked">Sender Account Blocked. Fraud suspected.</option>
                            <option value="illegal_activity">Illegal activity / Violation of Terms of Service.</option>
                            <option value="pending_cancel">Cancel Pending Transfer — no balance change.</option>
                            <option value="recipient_report">Customer reported unauthorized transfer</option>
                            <option value="other">Other (specify)</option>
                        </Form.Select>

                        {reasonKey === "other" && (
                            <Form.Control
                                size="sm"
                                placeholder="Type reason…"
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                style={{ minWidth: 260 }}
                            />
                        )}
                    </div>
                    <small className="text-muted">
                        The reason will be saved to the transfer comment and logs.
                    </small>
                </div>
            </Modal.Body>

            <Modal.Footer className="d-flex align-items-center w-100">
                {!done ? (
                    <>
                        <Button
                            size="sm"
                            variant="danger"
                            disabled={cancelDisabled}
                            onClick={handleCancel}
                            className="me-auto"
                        >
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="me-1" /> Canceling…
                                </>
                            ) : (
                                "Cancel Transfer"
                            )}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={handleClose}>
                            Close
                        </Button>
                    </>
                ) : (
                    <>
                    <Button
                        size="sm"
                        variant="danger"
                        disabled={cancelDisabled}
                        onClick={handleCancel}
                        className="me-auto"
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-1" /> Canceling…
                            </>
                        ) : (
                            "Cancel was Successful"
                        )}
                    </Button>

                    <Button size="sm" variant="outline-secondary" onClick={handleClose}>
                Close
            </Button>
                    <Alert variant="success" className="py-1 mb-0 w-100 text-center">
                        Transfer canceled successfully.
                    </Alert>
                        {err && (
                            <Alert variant="success" className="py-1 mb-0 w-100 text-center">
                                {err}
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default CancelTransferModal;
